import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

// Define the shape of the context data
const CookieConsentContext = createContext({
  consent: null, // 'all', 'essential', or null
  hasMadeChoice: false,
  allowAnalytics: false,
  updateConsent: () => {},
  openSettings: () => {},
  closeSettings: () => {},
  isSettingsOpen: false,
});

// Custom hook for easy consumption of the context
export const useCookieConsent = () => useContext(CookieConsentContext);

// The provider component that will wrap the application
export const CookieConsentProvider = ({ children }) => {
  // State for the consent decision ('all', 'essential', or null if no choice made)
  const [consent, setConsent] = useState(null);
  // State to manage the visibility of the settings modal (to be built)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // On initial load, check localStorage for a saved decision
  useEffect(() => {
    // console.log('[CookieConsentContext] useEffect running to check localStorage.');
    const savedConsent = localStorage.getItem('cookie_consent_decision');
    if (savedConsent === 'all' || savedConsent === 'essential') {
      // console.log(`[CookieConsentContext] Found saved consent: ${savedConsent}`);
      setConsent(savedConsent);
    }
  }, []);

  // Function to update the consent and store it in localStorage
  const updateConsent = useCallback((decision) => {
    if (decision === 'all' || decision === 'essential') {
      // console.log(`[CookieConsentContext] Updating consent to: ${decision}`);
      localStorage.setItem('cookie_consent_decision', decision);
      setConsent(decision);
      setIsSettingsOpen(false); // Close modal on decision
    }
  }, []);

  const openSettings = useCallback(() => {
    setIsSettingsOpen(true);
  }, []);

  const closeSettings = useCallback(() => {
    setIsSettingsOpen(false);
  }, []);

  // Determine if analytics are allowed based on consent
  const allowAnalytics = consent === 'all';
  const hasMadeChoice = consent !== null;

  // console.log(`[CookieConsentContext] Rendering provider. hasMadeChoice: ${hasMadeChoice}, consent: ${consent}`);
  // The value to be provided to consuming components
  const value = {
    consent,
    hasMadeChoice,
    allowAnalytics,
    updateConsent,
    isSettingsOpen,
    openSettings,
    closeSettings,
  };

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
};
